/**
 * GoalTree - recursive Domain -> Program -> Goal/Target tree, styled as a
 * file-explorer / folder tree (indentation + vertical guide rail per nesting
 * level, expand/collapse chevrons, folder/leaf icons).
 *
 * Row actions (Edit/Change status/Delete/Add child) render as real sibling
 * <button>s next to the toggle — not nested inside it — because a toggle
 * button can't contain other interactive elements. Any Domain/Program can
 * still gain a deeper child at any time — even one that is currently `isLeaf`
 * (pickable, no children yet) — so the branch row (and its "+ Add child"
 * affordance) is shown unconditionally for those two levels, not gated on
 * isLeaf. Only Goal/Target is a true terminal leaf.
 */

import { memo, useState } from 'react';
import styled from 'styled-components';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Target,
  Plus,
  Pencil,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import type { GoalNode } from '@upskill/shared';
import { Badge } from '../../components/ui';
import { media } from '../../styles/media';
import { LEVEL_LABELS, NEXT_LEVEL, STATUS_BADGE_VARIANT, STATUS_LABELS } from './statusMeta';

// ==================== Styled ====================

const TreeRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-x: auto;
`;

/** Nested children get an indent + a left "rail" guide line; the root list doesn't. */
const ChildrenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 11px;
  padding-left: ${({ theme }) => theme.spacing[3]};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const AddChildButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  margin-top: 2px;
  border: 1px dashed ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: none;
  color: ${({ theme }) => theme.colors.primary.base};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  min-height: 44px;

  &:hover {
    background: ${({ theme }) => theme.colors.status.info.bg};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ---- Row shared by branch (Domain/Program) and leaf (Goal/Target) ----

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  min-height: 44px;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LeafSpacer = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
`;

const NodeIcon = styled.span<{ $variant: 'domain' | 'program' | 'leaf' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme, $variant }) =>
    $variant === 'domain'
      ? theme.colors.status.info.text
      : $variant === 'program'
        ? theme.colors.text.secondary
        : theme.colors.text.light};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const NodeMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  flex: 1;
  min-width: 160px;
`;

const NodeNameRow = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const NodeName = styled.span<{ $branch?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme, $branch }) =>
    $branch ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const ChildCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
`;

const PickableMarker = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error.text};
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MetaRow = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const RowActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  align-items: center;
  flex-shrink: 0;

  ${media.tablet} {
    width: 100%;
    justify-content: flex-end;
  }
`;

const IconButton = styled.button<{ $variant?: 'default' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme, $variant }) =>
    $variant === 'danger' ? theme.colors.status.error.text : theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === 'danger' ? theme.colors.status.error.bg : theme.colors.backgrounds.gray};
    color: ${({ theme, $variant }) =>
      $variant === 'danger' ? theme.colors.status.error.text : theme.colors.text.primary};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

// ==================== Shared row bits ====================

/** shortTermNote/dateInitiated/dateMet — shown under both leaf and branch node names. */
function GoalMetaRow({ node }: { node: GoalNode }) {
  if (!node.shortTermNote && !node.dateInitiated && !node.dateMet) return null;
  return (
    <MetaRow>
      {node.shortTermNote && <span>Short-term: {node.shortTermNote}</span>}
      {node.dateInitiated && <span>Initiated {node.dateInitiated.slice(0, 10)}</span>}
      {node.dateMet && <span>Met {node.dateMet.slice(0, 10)}</span>}
    </MetaRow>
  );
}

// ==================== Component ====================

export interface GoalTreeProps {
  nodes: GoalNode[];
  canManage: boolean;
  onAddChild: (parent: GoalNode | null) => void;
  onEdit: (goal: GoalNode) => void;
  onChangeStatus: (goal: GoalNode) => void;
  onDelete: (goal: GoalNode) => void;
}

/** Root-level tree — one entry per top-level node (typically Domains). */
export function GoalTree({ nodes, canManage, onAddChild, onEdit, onChangeStatus, onDelete }: GoalTreeProps) {
  return (
    <TreeRoot>
      {nodes.map((node) => (
        <GoalTreeNode
          key={node.id}
          node={node}
          canManage={canManage}
          onAddChild={onAddChild}
          onEdit={onEdit}
          onChangeStatus={onChangeStatus}
          onDelete={onDelete}
        />
      ))}
    </TreeRoot>
  );
}

interface GoalTreeNodeProps {
  node: GoalNode;
  canManage: boolean;
  onAddChild: (parent: GoalNode | null) => void;
  onEdit: (goal: GoalNode) => void;
  onChangeStatus: (goal: GoalNode) => void;
  onDelete: (goal: GoalNode) => void;
}

const GoalTreeNode = memo(function GoalTreeNode({
  node,
  canManage,
  onAddChild,
  onEdit,
  onChangeStatus,
  onDelete,
}: GoalTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const nextLevel = NEXT_LEVEL[node.level];

  // Goal/Target is the only truly terminal level — it can never gain children.
  if (node.level === 'GOAL_TARGET') {
    return (
      <Row>
        <LeafSpacer aria-hidden="true" />
        <NodeIcon $variant="leaf" aria-hidden="true">
          <Target />
        </NodeIcon>
        <NodeMain>
          <NodeNameRow>
            <NodeName>{node.name}</NodeName>
            <Badge variant={STATUS_BADGE_VARIANT[node.status]}>{STATUS_LABELS[node.status]}</Badge>
            {node.isPickable && (
              <PickableMarker title="Red-Dot selectable">
                <Target /> Red-Dot
              </PickableMarker>
            )}
          </NodeNameRow>
          <GoalMetaRow node={node} />
        </NodeMain>

        {canManage && (
          <RowActions>
            <IconButton title="Edit goal" aria-label="Edit goal" onClick={() => onEdit(node)}>
              <Pencil />
            </IconButton>
            <IconButton
              title="Change status"
              aria-label="Change status"
              onClick={() => onChangeStatus(node)}
            >
              <RefreshCw />
            </IconButton>
            <IconButton
              $variant="danger"
              title="Delete goal"
              aria-label="Delete goal"
              onClick={() => onDelete(node)}
            >
              <Trash2 />
            </IconButton>
          </RowActions>
        )}
      </Row>
    );
  }

  // Domain/Program — always rendered as an expandable branch with row actions,
  // regardless of node.isLeaf. A childless Domain/Program is still `isLeaf` on
  // the backend (no children yet), but the user must always be able to add a
  // child under it — that's the whole point of building out the tree.
  const iconVariant = node.level === 'DOMAIN' ? 'domain' : 'program';

  return (
    <div>
      <Row>
        <ToggleButton
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
          type="button"
        >
          {isExpanded ? <ChevronDown /> : <ChevronRight />}
        </ToggleButton>
        <NodeIcon $variant={iconVariant} aria-hidden="true">
          {isExpanded ? <FolderOpen /> : <Folder />}
        </NodeIcon>
        <NodeMain>
          <NodeNameRow>
            <NodeName $branch>{node.name}</NodeName>
            <ChildCount aria-label={`${node.children.length} children`}>
              {node.children.length}
            </ChildCount>
            {node.isPickable && (
              <>
                <Badge variant={STATUS_BADGE_VARIANT[node.status]}>{STATUS_LABELS[node.status]}</Badge>
                <PickableMarker title="Red-Dot selectable">
                  <Target /> Red-Dot
                </PickableMarker>
              </>
            )}
          </NodeNameRow>
          {node.isPickable && <GoalMetaRow node={node} />}
        </NodeMain>

        {canManage && (
          <RowActions>
            <IconButton
              title={`Edit ${LEVEL_LABELS[node.level]}`}
              aria-label={`Edit ${node.name}`}
              onClick={() => onEdit(node)}
            >
              <Pencil />
            </IconButton>
            {node.isPickable && (
              <IconButton
                title="Change status"
                aria-label={`Change status for ${node.name}`}
                onClick={() => onChangeStatus(node)}
              >
                <RefreshCw />
              </IconButton>
            )}
            <IconButton
              $variant="danger"
              title={`Delete ${LEVEL_LABELS[node.level]}`}
              aria-label={`Delete ${node.name}`}
              onClick={() => onDelete(node)}
            >
              <Trash2 />
            </IconButton>
          </RowActions>
        )}
      </Row>

      {/* Skip mounting the (potentially large) nested subtree while collapsed. */}
      {isExpanded && (node.children.length > 0 || (canManage && nextLevel)) && (
        <ChildrenList>
          {node.children.map((child) => (
            <GoalTreeNode
              key={child.id}
              node={child}
              canManage={canManage}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onChangeStatus={onChangeStatus}
              onDelete={onDelete}
            />
          ))}
          {canManage && nextLevel && (
            <AddChildButton onClick={() => onAddChild(node)}>
              <Plus /> Add {LEVEL_LABELS[nextLevel]}
            </AddChildButton>
          )}
        </ChildrenList>
      )}
    </div>
  );
});

export default GoalTree;
